import { loadStripe } from '@stripe/stripe-js';
import React, { useContext } from 'react';
import { useQueryClient } from 'react-query';

import { ModalType } from '@/API/models';
import BaseModal from '@/components/Modals/BaseModal';
import { EditProfileContext } from '@/contexts/EditProfileProvider';
import { useCurrentUser } from '@/Utils/stripeHooks/useCurrentUser';
import { usePaymentMethods } from '@/Utils/stripeHooks/usePaymentMethods';
import { useSubscriptionCheckout } from '@/Utils/stripeHooks/useSubscriptionCheckout';
import { updatedSubscriptionAlertMessageOptions } from '@/Utils/useAlertMessage';

const UpgradeModal = ({
  handleOpenModal,
  selectedPlanPriceId,
}: {
  handleOpenModal: (type: ModalType) => void;
  selectedPlanPriceId: string;
}) => {
  const { stripeKey, showAlert } = useContext(EditProfileContext);
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();

  const { mutateAsync: checkout, isLoading } = useSubscriptionCheckout({
    onSuccess: async (res) => {
      let errorMessage: string | null = null;
      if (stripeKey && res && 'payment_intent_client_secret' in res) {
        const stripe = await loadStripe(stripeKey);
        const confirmRes = await stripe?.confirmCardPayment(
          res.payment_intent_client_secret,
        );

        if (confirmRes?.error?.message) {
          errorMessage = confirmRes.error.message;
        } 
      }

      queryClient.invalidateQueries(['currentUser']);
      handleOpenModal(null);

      if (errorMessage) {
        showAlert({ type: 'error', description: errorMessage });
      } else {
        showAlert(updatedSubscriptionAlertMessageOptions);
      }
    },
  });

  const { data: paymentMethods } = usePaymentMethods();
  const defaultPaymentMethod = paymentMethods?.find((pm) => pm.default);

  const handleUpgrade = async () => {
    if (!user?.phone_verified_at || !user.phone) {
      document.cookie = `subscription_price_id=${selectedPlanPriceId}; path=/; Secure`;
      window.location.href = '/users/phone_number_verification';
    } else {
      await checkout({ price_id: selectedPlanPriceId });
    }
  };

  return (
    <BaseModal
      icon='/icons/warning-full.svg'
      title='Payment Confirmation'
      text={
        <>
          <span className='leading-24px mb-10 inline-block'>
            Your available balance will be used first and any remaining balance
            will be charged to your default payment method on file{' '}
            {defaultPaymentMethod && (
              <>
                <span>****</span>
                <span>{defaultPaymentMethod.last4}</span>
              </>
            )}
          </span>

          <span>
            In case of an active paid subscription, your payment may be
            prorated.
          </span>
        </>
      }
      cancelButton
      primaryButtonText='Upgrade'
      handlePrimaryButton={handleUpgrade}
      handleCancel={() => handleOpenModal(null)}
      isLoading={isLoading}
    />
  );
};
export default UpgradeModal;
